<template>
  <div class="maintenance-form">
    <validation-observer ref="formValidation">
      <!-- Description Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.description')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.description')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-textarea
            v-model="formData.description"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterDescription')"
            maxlength="300"
          />
          <div class="d-flex justify-content-between align-items-center mt-1">
            <small class="text-muted"
              >{{ formData.description.length }}/300</small
            >
            <small class="text-danger">{{ errors[0] }}</small>
          </div>
        </b-form-group>
      </validation-provider>

      <!-- Attachments (Optional) -->
      <b-form-group class="mb-2">
        <label>
          <span>{{ $t('field.attachments') }}</span>
          <span class="text-muted"> ({{ $t('field.optional') }}) </span>
        </label>
        <n-multiple-file-uploader
          v-model="uploadedFiles"
          class="mb-1"
          @files="onFiles"
          :allow-clipboard="true"
        />
      </b-form-group>

      <!-- Maintenance Location Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.address')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.maintenanceLocation')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <div v-if="hasAddressOptions">
            <!-- <b-form-radio-group
              v-model="addressMode"
              :options="addressModeOptions"
              :state="errors.length === 0 ? null : false"
              class="mb-1"
            /> -->

            <n-single-select
              v-if="addressMode === 'rental'"
              v-model="formData.address"
              :options="rentalAddressOptions"
              :selection-label="'text'"
              :selection-key="'value'"
              :reduce="true"
              :clearable="false"
              :placeholder="$t('placeholder.selectLocation')"
            />

            <b-form-textarea
              v-else
              v-model="customAddress"
              :rows="3"
              :state="errors.length === 0 ? null : false"
              :placeholder="$t('placeholder.enterMaintenanceLocation')"
              class="mt-1"
            />
          </div>
          <b-form-textarea
            v-else
            v-model="customAddress"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterMaintenanceLocation')"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Special Instructions Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.specialInstructions')"
        rules=""
      >
        <b-form-group
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <label>
            <span>{{ $t('field.specialInstructions') }}</span>
            <span class="text-muted"> ({{ $t('field.optional') }}) </span>
          </label>
          <b-form-textarea
            v-model="formData.specialInstructions"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterSpecialInstructions')"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      

      <!-- Service Time Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.scheduleType')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.maintenanceTime')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-radio-group
            v-model="formData.scheduleType"
            :options="[
              { text: $t('field.immediately'), value: 1 },
              { text: $t('field.customTime'), value: 2 }
            ]"
            :state="errors.length === 0 ? null : false"
          />
          <n-date-time-picker
            v-if="formData.scheduleType === 2"
            v-model="formData.scheduleDate"
            :state="errors.length === 0 ? null : false"
            :disable-before-today="true"
            :disable-after-today="false"
            class="mt-2"
            style="width: 50%"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>
    </validation-observer>

    <!-- File Selection Modal -->
    <file-selection-modal
      v-model="isFileModalVisible"
      :file-type="currentFileType"
      :files="filteredFiles"
      @select="confirmFileSelection"
    />
  </div>
</template>

<script>
  import { BFormGroup, BFormRadioGroup, BFormTextarea } from 'bootstrap-vue';
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import NDateTimePicker from '@/components/NDateTimePicker.vue';
  import FileSelectionModal from '@/components/FileSelectionModal.vue';
  import FileSelectionCard from '@/components/FileSelectionCard.vue';
  import RepositoryFactory from '@/repositories/RepositoryFactory';
  import NSingleSelect from '@/components/NSingleSelect.vue';
  import NMultipleFileUploader from '@/components/NMultipleFileUploader.vue';

  const ChatMessageRepository = RepositoryFactory.get('chatMessage');

  export default {
    name: 'MaintenanceForm',

    components: {
      BFormGroup,
      BFormRadioGroup,
      BFormTextarea,
      ValidationProvider,
      ValidationObserver,
      NDateTimePicker,
      FileSelectionModal,
      FileSelectionCard,
      NSingleSelect,
      NMultipleFileUploader
    },

    props: {
      value: {
        type: Object,
        default: () => ({})
      },
      chatChannelId: {
        type: String,
        required: false
      },
      fullAddressList: {
        type: Array,
        default: () => []
      },
      allowClipboard: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        formData: {
          description: '',
          files: [],
          address: null,
          specialInstructions: '',
          scheduleType: 1,
          scheduleDate: '',
        },
        addressMode: 'other',
        isFileModalVisible: false,
        currentFileType: null,
        chatFiles: [],
        filteredFiles: [],
        uploadedFiles: []
      };
    },

    computed: {
      addressOptions() {
        const list = Array.isArray(this.fullAddressList)
          ? this.fullAddressList
          : [];
        const options = list.map(item => ({
          text: this.getAddressLabel(item),
          value: item
        }));
        if (options.length > 0) {
          options.push({
            text: this.$t('field.other'),
            value: {
              isCustom: true,
              typeKey: 'other',
              typeLabel: this.$t('field.other'),
              addressName: null,
              customAddress: ''
            }
          });
        }
        return options;
      },
      rentalAddressOptions() {
        const list = Array.isArray(this.fullAddressList)
          ? this.fullAddressList
          : [];
        return list.map(item => ({
          text: this.getAddressLabel(item),
          value: item
        }));
      },
      hasAddressOptions() {
        return this.addressOptions.length > 0;
      },
      addressModeOptions() {
        return [
          { text: this.$t('general.rentalAddress'), value: 'rental' },
          { text: this.$t('general.otherAddress'), value: 'other' }
        ];
      },
      customAddress: {
        get() {
          if (
            this.formData.address &&
            (this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            return this.formData.address.customAddress || '';
          }
          return '';
        },
        set(val) {
          this.formData.address = {
            isCustom: true,
            typeKey: 'other',
            typeLabel: this.$t('field.other'),
            addressName: null,
            customAddress: val,
            files: [],
          };
        }
      }
    },

    watch: {
      value: {
        handler(newValue) {
          if (
            newValue &&
            JSON.stringify(newValue) !== JSON.stringify(this.formData)
          ) {
            this.formData = {
              description: newValue.description ?? '',
              files: newValue.files ?? [],
              address: newValue.address ?? null,
              specialInstructions: newValue.specialInstructions ?? '',
              scheduleType: newValue.scheduleType ?? 1,
              scheduleDate: newValue.scheduleDate ?? ''
            };
            this.addressMode =
              this.formData.address &&
              (this.formData.address.isCustom ||
                this.formData.address.typeKey === 'other')
                ? 'other'
                : this.hasAddressOptions
                ? 'rental'
                : 'other';
          }
        },
        immediate: true,
        deep: true
      },
      formData: {
        handler(newValue) {
          if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
            const sanitizedValue = { ...newValue };
            this.$emit('input', sanitizedValue);
          }
        },
        deep: true
      },
      fullAddressList: {
        handler() {
          this.formData.address = null;
          this.addressMode = this.hasAddressOptions ? 'rental' : 'other';
        }
      },
      addressMode(val) {
        if (val === 'rental') {
          if (
            this.formData.address &&
            (this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            this.formData.address = null;
          }
        } else if (val === 'other') {
          if (
            this.formData.address &&
            !(this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            this.formData.address = null;
          }
        }
      }
    },

    mounted() {
      this.getAttachments();
    },

    methods: {
      onFiles(files) {
        this.uploadedFiles = files;
        this.syncUploadedFiles();
      },
      syncUploadedFiles() {
        this.formData.files = [...this.uploadedFiles];
      },
      getAddressLabel(item) {
        if (!item) return ''
        const locale = this.getLocale()
        const parts = []
        
        // Handle localized typeLabel - add to front with dash
        let typeLabel = ''
        if (item.typeLabel) {
          if (typeof item.typeLabel === 'object') {
            typeLabel = item.typeLabel[locale] 
              || item.typeLabel.en 
              || item.typeLabel.zh 
              || item.typeLabel.km 
              || item.typeLabel.vi 
              || ''
          } else {
            typeLabel = item.typeLabel
          }
          if (typeLabel) parts.push(`${typeLabel}-`)
        }
        
        // Handle localized addressName from translation object
        const addressName = item.translation?.addressName?.[locale] || 
          item.translation?.addressName?.en ||
          item.translation?.addressName?.zh ||
          item.translation?.addressName?.km ||
          item.translation?.addressName?.vi ||
          item.addressName ||
          ''
        
        if (addressName) parts.push(addressName)
        
        return parts.join('')
      },
      getLocale() {
        const [locale] = this.$i18n.locale.split('-')
        return locale || 'en'
      },
      async validate() {
        if (this.$refs.formValidation) {
          const isValid = await this.$refs.formValidation.validate();

          return isValid;
        }
        return false;
      },

      reset() {
        this.formData = {
          description: '',
          files: [],
          address: null,
          specialInstructions: '',
          scheduleType: 1,
          scheduleDate: null
        };
        if (this.$refs.formValidation) {
          this.$refs.formValidation.reset();
        }
      },

      openFileModal(type) {
        this.currentFileType = type;
        // Filter files by type only
        this.filteredFiles = this.chatFiles.filter(
          file => file.type === this.currentFileType
        );
        this.isFileModalVisible = true;
      },

      confirmFileSelection(selectedFile) {
        if (!selectedFile || !this.currentFileType) {
          return;
        }

        // Remove any existing file of this type
        this.removeFileByType(this.currentFileType);

        // Add the selected file with only essential properties
        this.formData.files.push({
          originalName: selectedFile.name,
          type: this.currentFileType,
          mimeType: selectedFile.mimeType,
          url: selectedFile.url
        });
      },

      hasFileType(type) {
        return this.formData.files.some(file => file.type === type);
      },

      getFileByType(type) {
        return this.formData.files.find(file => file.type === type) || null;
      },

      removeFileByType(type) {
        const index = this.formData.files.findIndex(file => file.type === type);
        if (index !== -1) {
          this.formData.files.splice(index, 1);
        }
      },

      getAttachments() {
        if (!this.chatChannelId) {
          return;
        }
        ChatMessageRepository.getAttachments(this.chatChannelId)
          .then(response => {
            // Handle different response structures
            const attachments = Array.isArray(response.data)
              ? response.data
              : response.data?.data || [];

            // Filter only image, video, and voice file types
            const filteredAttachments = attachments.filter(attachment => {
              const mediaType = attachment.mediaType || '';
              return (
                mediaType.startsWith('image/') ||
                mediaType.startsWith('video/') ||
                mediaType.startsWith('audio/')
              );
            });

            // Map API response data to the format expected by the component
            // Only include essential properties: mimeType, url, type, and originalName
            this.chatFiles = filteredAttachments.map(attachment => {
              // Determine file type based on mediaType
              let type = 'photo'; // Default to photo
              if (attachment.mediaType) {
                if (attachment.mediaType.startsWith('video/')) {
                  type = 'video';
                } else if (attachment.mediaType.startsWith('audio/')) {
                  type = 'voice';
                }
              }

              // Only include essential properties for the file selection
              return {
                id: attachment.id, // Keep id for internal reference only
                name: attachment.message || `file-${attachment.id}`,
                mimeType: attachment.mediaType || '',
                date: new Date(attachment.createdAt), // Keep date for display purposes
                type,
                url: attachment.mediaUrl || ''
              };
            });

            console.log('chatFiles', this.chatFiles);
          })
          .catch(error => {
            console.error('Error fetching attachments:', error);
          });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .maintenance-form {
    .form-group:last-child {
      margin-bottom: 0;
    }

    .media-section {
      h6 {
        font-weight: 600;
        color: #495057;
      }

      .media-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 1rem;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
        }
      }
    }
  }
</style>
